class CartUpsellSystem {
  constructor() {
    this.drawer = document.getElementById('waite-cart');
    if (!this.drawer) return;
    this.strings = JSON.parse(this.drawer.querySelector('[data-wc-strings]').textContent);
    this.root = this.drawer.dataset.root || '/';
    this.threshold = Number(this.drawer.dataset.threshold);
    this.currency = this.drawer.dataset.currency || 'USD';
    this.roundProductLabels = this.drawer.dataset.roundProductLabels === 'true';
    this.busy = false;
    this.loadVersion = 0;
    this.recommendationVersion = 0;
    this.drawer.addEventListener('click', event => this.onClick(event));
    this.drawer.addEventListener('close', () => {
      document.body.style.overflow = this.oldOverflow || '';
      if (this.opener?.isConnected) this.opener.focus();
    });
    this.drawer.addEventListener('submit', event => { if (this.busy) event.preventDefault(); });
    document.addEventListener('click', event => {
      const link = event.target.closest('a[data-cart-link], a[href]');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      const path = new URL(link.href, location.href).pathname;
      if (!link.hasAttribute('data-cart-link') && path !== this.root + 'cart') return;
      if (link.closest('#waite-cart') || link.target === '_blank') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.openCart(link);
    }, true);
    document.addEventListener('cart:updated', () => this.refreshCart());
  }
  el(selector) { return this.drawer.querySelector(selector); }
  text(tag, text, className) {
    const el = document.createElement(tag);
    el.textContent = text;
    if (className) el.className = className;
    return el;
  }
  money(cents) { return new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency', currency:this.currency, minimumFractionDigits:cents % 100 === 0 ? 0 : 2, maximumFractionDigits:2}).format(cents / 100); }

  productMoney(cents) {
    return this.roundProductLabels && this.currency === 'USD'
      ? new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency', currency:this.currency, minimumFractionDigits:0, maximumFractionDigits:0}).format(cents / 100)
      : this.money(cents);
  }
  async request(path, body) {
    const response = await fetch(this.root + path, body ? {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(body)} : {headers:{'Accept':'application/json'},cache:'no-store'});
    const data = await response.json();
    if (!response.ok || data.status >= 400) throw new Error(typeof data.description === 'string' ? data.description : this.strings.error);
    return data;
  }
  async openCart(opener) {
    if (!this.drawer.open) {
      this.opener = opener || document.activeElement;
      this.oldOverflow = document.body.style.overflow;
      this.drawer.showModal();
      document.body.style.overflow = 'hidden';
      this.el('[data-wc-close]').focus();
    }
    await this.loadCart();
  }
  closeCart() { this.drawer.close(); }
  refreshCart() { if (this.drawer.open && !this.busy) return this.loadCart(); }
  async loadCart() {
    const version = ++this.loadVersion;
    if (!this.currentCart) this.el('[data-wc-items]').replaceChildren(this.text('p',this.strings.loading,'wc-loading'));
    this.error('');
    try {
      const cart = await this.request('cart.js');
      if (version !== this.loadVersion) return;
      this.renderCart(cart);
      this.loadRecommendations(cart);
    } catch (error) { this.error(error.message); }
  }
  error(message) {
    const el = this.el('[data-wc-error]');
    el.textContent = message;
    el.hidden = !message;
  }
  setBusy(value) {
    this.busy = value;
    this.el('[data-wc-items]').setAttribute('aria-busy', String(value));
    this.drawer.querySelectorAll('button:not([data-wc-close])').forEach(button => { button.disabled = value; });
  }
  renderCart(cart) {
    this.currentCart = cart;
    this.currency = cart.currency || this.currency;
    this.el('[data-wc-count]').textContent = `(${cart.item_count})`;
    document.querySelectorAll('.wh-bag').forEach(link => link.setAttribute('aria-label', `Bag (${cart.item_count})`));
    document.querySelectorAll('.cart-count-badge').forEach(badge => {badge.textContent = cart.item_count > 99 ? '+' : cart.item_count; badge.classList.toggle('hidden', !cart.item_count);});
    const items = this.el('[data-wc-items]');
    items.replaceChildren();
    this.el('[data-wc-footer]').hidden = !cart.item_count;
    this.el('[data-wc-total]').textContent = this.money(cart.total_price);
    if (!cart.item_count) {
      const empty = this.text('div','','wc-empty');
      empty.append(this.text('p',this.strings.empty));
      const button = this.text('button',this.strings.continue);
      button.type = 'button'; button.dataset.wcClose = '';
      empty.append(button); items.append(empty);
    }
    for (const item of cart.items) items.append(this.lineItem(item));
    const progress = this.el('.wc-track');
    const eligibleCurrency = this.currency === 'USD';
    const percentage = Math.min(100,Math.max(0,cart.total_price / this.threshold * 100));
    progress.hidden = !eligibleCurrency;
    this.el('.wc-shipping small').hidden = !eligibleCurrency;
    progress.setAttribute('aria-valuenow',String(Math.round(percentage)));
    this.el('[data-wc-progress]').style.width = percentage + '%';
    this.el('[data-wc-shipping]').textContent = !eligibleCurrency ? this.strings.shippingCheckout : cart.total_price >= this.threshold ? this.strings.unlocked : this.strings.remaining.replace('[amount]',this.money(this.threshold - cart.total_price));
    this.el('[data-wc-status]').textContent = this.strings.updated;
  }
  lineItem(item) {
    const row = this.text('article','','wc-item'); row.dataset.key = item.key;
    const image = document.createElement('img'); image.className = 'wc-item-image'; image.alt = item.product_title; image.width = 80; image.height = 98;
    if (item.image) image.src = item.image;
    const details = document.createElement('div');
    const title = this.text('a',item.product_title,'wc-item-title'); title.href = item.url; details.append(title);
    if (item.variant_title && item.variant_title !== 'Default Title') details.append(this.text('p',item.variant_title,'wc-item-meta'));
    if (item.selling_plan_allocation) details.append(this.text('p',item.selling_plan_allocation.selling_plan.name,'wc-item-meta'));
    for (const [key,value] of Object.entries(item.properties || {})) if (!key.startsWith('_') && value) details.append(this.text('p',`${key}: ${value}`,'wc-item-meta'));
    const quantity = this.text('div','','wc-quantity');
    for (const change of [-1,1]) {
      const button = this.text('button',change < 0 ? '−' : '+'); button.type='button'; button.dataset.delta=change;
      button.setAttribute('aria-label',`${change < 0 ? this.strings.decrease : this.strings.increase}: ${item.product_title}`);
      if (change > 0) quantity.append(this.text('span',String(item.quantity)));
      quantity.append(button);
    }
    details.append(quantity);
    const remove = this.text('button',this.strings.remove,'wc-remove'); remove.type='button'; remove.dataset.remove=''; remove.setAttribute('aria-label',`${this.strings.remove}: ${item.product_title}`); details.append(remove);
    const price = this.text('div','','wc-price');
    if (item.original_line_price > item.final_line_price) price.append(this.text('del',this.productMoney(item.original_line_price)));
    price.append(this.text('span',this.productMoney(item.final_line_price)));
    for (const discount of item.line_level_discount_allocations || []) details.append(this.text('p',discount.discount_application.title,'wc-discounts'));
    row.append(image,details,price); return row;
  }
  async onClick(event) {
    const button = event.target.closest('button');
    if (button?.hasAttribute('data-wc-close')) return this.closeCart();
    if (event.target === this.drawer) {
      const r=this.drawer.getBoundingClientRect();
      if(event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.closeCart();
      return;
    }
    if (!button || this.busy) return;
    const row = button.closest('[data-key]');
    if (row && (button.hasAttribute('data-delta') || button.hasAttribute('data-remove'))) {
      const item = this.currentCart.items.find(item => item.key === row.dataset.key);
      if (!item) return;
      const quantity = button.hasAttribute('data-remove') ? 0 : Math.max(0,item.quantity + Number(button.dataset.delta));
      await this.mutate('cart/change.js',{id:item.key,quantity},item.key,button.dataset.delta);
    } else if (button.dataset.variant) {
      await this.mutate('cart/add.js',{items:[{id:Number(button.dataset.variant),quantity:1}]});
    }
  }
  async mutate(path,body,key,delta) {
    this.setBusy(true); this.error(''); ++this.loadVersion;
    try {
      await this.request(path,body);
      const cart=await this.request('cart.js');
      this.renderCart(cart); this.loadRecommendations(cart); this.setBusy(false);
      const replacement=[...this.drawer.querySelectorAll('[data-key]')].find(el=>el.dataset.key===key);
      (replacement?.querySelector(`[data-delta="${delta}"]`) || this.el('[data-wc-close]')).focus();
    } catch(error) { this.error(error.message); }
    finally { this.setBusy(false); }
  }
  async loadRecommendations(cart) {
    const version=++this.recommendationVersion;
    const wrapper=this.el('[data-wc-recs]');
    wrapper.hidden=true;
    this.el('[data-wc-products]').replaceChildren();
    if (!cart.items.length) return;
    const ids=new Set(cart.items.map(item=>item.product_id));
    const eligible = products => products.filter(product=>product.available && !product.requires_selling_plan && !ids.has(product.id) && product.variants?.some(v=>v.available));
    try {
      let response=await this.request(`recommendations/products.json?product_id=${cart.items[0].product_id}&limit=10&intent=complementary`);
      let products=eligible(response.products || []);
      if (!products.length) {
        response=await this.request(`recommendations/products.json?product_id=${cart.items[0].product_id}&limit=10&intent=related`);
        products=eligible(response.products || []);
      }
      if (version!==this.recommendationVersion) return;
      for(const product of products.slice(0,2)) this.el('[data-wc-products]').append(this.recommendation(product));
      wrapper.hidden=!products.length;
    } catch { wrapper.hidden=true; }
  }
  recommendation(product) {
    const row=this.text('article','','wc-rec');
    const image=document.createElement('img'); image.src=product.featured_image || product.images[0]; image.alt=product.title; image.loading='lazy'; image.width=64; image.height=76;
    const details=document.createElement('div'); const title=this.text('a',product.title); title.href=product.url; details.append(title);
    const variant=product.variants.find(v=>v.available);
    details.append(this.text('p',this.productMoney(variant.price)));
    let action;
    if (product.variants.length===1) { action=this.text('button',this.strings.add); action.type='button'; action.dataset.variant=variant.id; action.setAttribute('aria-label',`${this.strings.add}: ${product.title}`); }
    else { action=this.text('a',this.strings.choose,'wc-choose'); action.href=product.url; }
    row.append(image,details,action); return row;
  }
}
let cartUpsell;
document.addEventListener('DOMContentLoaded',()=>{cartUpsell=new CartUpsellSystem();window.cartUpsell=cartUpsell;});
